<template>
  <PjSheet
    :label="props.item?.name ?? ''"
    :open="visible && props.item !== null"
    @close="visible = false"
  >
    <div
      v-if="props.item"
      class="flex flex-col gap-5 px-page-x pb-2"
    >
      <!-- The box stands on whether the dish HAS a photograph, not on whether
           it loaded. Collapsing it on error meant the sheet opened, reserved a
           full-width square, then snapped shut around it the moment the request
           failed — the same reflow the menu rows had. -->
      <div
        v-if="props.item.imageUrl"
        class="aspect-square w-full overflow-hidden rounded-art bg-surface"
      >
        <img
          v-if="!imageFailed"
          alt=""
          class="h-full w-full object-cover"
          :src="props.item.imageUrl"
          @error="imageFailed = true"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <p
          v-if="props.item.description"
          class="text-body text-muted"
        >
          {{ props.item.description }}
        </p>
        <p class="pj-num text-price-l text-ink">{{ priceLabel }}</p>
      </div>

      <section
        v-for="group in optionGroups"
        :key="group.id"
        class="flex flex-col gap-1"
      >
        <header class="flex items-baseline justify-between gap-2 border-t pt-3">
          <h3 class="text-heading text-ink">{{ group.name }}</h3>
          <!-- An unmet requirement reads as instruction, not error. No red. -->
          <span
            class="shrink-0 text-caption"
            :class="selection.visibleErrors.value.get(group.id) ? 'font-semibold text-ink' : 'text-muted'"
          >
            {{ selection.visibleErrors.value.get(group.id) ?? hintFor(group) }}
          </span>
        </header>

        <ul class="divide-y">
          <li
            v-for="addon in group.items"
            :key="addon.id"
            class="flex items-center gap-3"
          >
            <button
              :aria-pressed="selection.isSelected(addon.id)"
              class="pj-tap-row group flex min-h-tap min-w-0 flex-1 items-center gap-3 py-2.5 text-left disabled:opacity-40"
              :disabled="isDisabled(group, addon.id)"
              type="button"
              @click="selection.toggleAddon(group, addon.id)"
            >
              <!-- One hairline and nothing else at rest. The 2px ring over a grey
                   centre read as a heavy, already-disabled control; at 1px on a
                   transparent centre the box is just an outline, and all the
                   weight arrives when it is actually selected. -->
              <span
                aria-hidden="true"
                class="flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-base"
                :class="[
                  group.type === ADDON_GROUP_TYPE.SINGLE ? 'rounded-full' : 'rounded-control',
                  selection.isSelected(addon.id)
                    ? 'border-primary bg-primary text-on-primary'
                    : 'border-outline bg-transparent group-hover:border-ink'
                ]"
              >
                <!-- A radio reads as a disc rather than a donut: at 20px with a
                     1px ring, a hollow centre and a filled centre are hard to
                     tell apart at a glance, and this is a choice people make
                     quickly. The checkbox carries the tick. -->
                <span
                  v-if="selection.isSelected(addon.id) && group.type === ADDON_GROUP_TYPE.SINGLE"
                  class="h-1.5 w-1.5 rounded-full bg-on-primary"
                />
                <PjIcon
                  v-else-if="selection.isSelected(addon.id)"
                  class="h-3 w-3"
                  :name="PJ_ICON.CHECK"
                />
              </span>

              <span class="min-w-0 flex-1 truncate text-body text-ink">{{ addon.name }}</span>

              <span
                v-if="addon.price.amount > 0"
                class="pj-num shrink-0 text-price text-muted"
              >
                + {{ addon.price.formatted }}
              </span>
            </button>

            <!-- Only an editable option gets a count. One that follows the dish
                 is always one each, so a stepper there would offer a choice the
                 server refuses. -->
            <span
              v-if="addon.isQuantityEditable && selection.isSelected(addon.id)"
              class="flex shrink-0 items-center rounded-full bg-surface"
            >
              <button
                :aria-label="`One fewer ${addon.name}`"
                class="flex h-10 w-9 items-center justify-center rounded-full text-muted pj-tap hover:text-ink"
                type="button"
                @click="selection.setAddonQuantity(addon, selection.quantityFor(addon) - 1)"
              >
                <PjIcon :name="PJ_ICON.MINUS" />
              </button>
              <span class="pj-num w-5 text-center text-title text-ink">{{ selection.quantityFor(addon) }}</span>
              <button
                :aria-label="`One more ${addon.name}`"
                class="flex h-10 w-9 items-center justify-center rounded-full text-ink pj-tap disabled:opacity-40 hover:text-primary"
                :disabled="selection.quantityFor(addon) >= selection.maxQuantityFor(addon)"
                type="button"
                @click="selection.setAddonQuantity(addon, selection.quantityFor(addon) + 1)"
              >
                <PjIcon :name="PJ_ICON.ADD" />
              </button>
            </span>
          </li>
        </ul>
      </section>

      <!-- Special instructions / note for this item -->
      <div
        v-if="canAdd"
        class="pt-2"
      >
        <label class="pj-field flex items-center gap-3 rounded-l bg-surface px-4 py-3">
          <PjIcon
            class="text-muted"
            :name="PJ_ICON.NOTE"
          />
          <span class="sr-only">Special instructions</span>
          <input
            v-model="selection.notes.value"
            class="min-w-0 flex-1 bg-transparent text-body text-ink placeholder:text-muted focus:outline-none"
            placeholder="Add special instructions"
            type="text"
          />
        </label>
      </div>
    </div>

    <template #footer>
      <div
        v-if="canAdd"
        class="flex items-center gap-3"
      >
        <!-- On a surface, so the stepper reads as one control rather than
             three loose glyphs floating on the sheet. -->
        <div class="flex shrink-0 items-center rounded-full bg-surface">
          <button
            aria-label="One fewer"
            class="flex h-btn-md w-11 items-center justify-center rounded-full text-muted pj-tap disabled:opacity-40 hover:text-ink"
            :disabled="selection.quantity.value <= 1"
            type="button"
            @click="selection.quantity.value -= 1"
          >
            <PjIcon :name="PJ_ICON.MINUS" />
          </button>
          <span class="pj-num w-5 text-center text-title text-ink">{{ selection.quantity.value }}</span>
          <button
            aria-label="One more"
            class="flex h-btn-md w-11 items-center justify-center rounded-full text-ink pj-tap hover:text-primary"
            type="button"
            @click="selection.quantity.value += 1"
          >
            <PjIcon :name="PJ_ICON.ADD" />
          </button>
        </div>

        <PjButton
          class="flex-1"
          @click="handleSave"
        >
          {{ saveLabel }}
        </PjButton>
      </div>
      <p
        v-else
        class="flex min-h-btn-md items-center justify-center text-center text-title text-ink"
      >
        {{ props.inVenueHint }}
      </p>
    </template>
  </PjSheet>
</template>

<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue'

import type { PjItemSheetProps } from '@/domains/pjazza/components/organisms/pj-item-sheet/PjItemSheet.types.ts'
import type { OrderingMenuAddonGroup } from '@/domains/pjazza/types/models/OrderingMenu.types.ts'

import PjButton from '@/domains/pjazza/components/atoms/pj-button/PjButton.vue'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import PjSheet from '@/domains/pjazza/components/organisms/pj-sheet/PjSheet.vue'
import { useItemSelection } from '@/domains/pjazza/composables/use-item-selection/useItemSelection.ts'
import { useBasketStore } from '@/domains/pjazza/store/use-basket-store/useBasketStore.ts'
import { useToastStore } from '@/domains/pjazza/store/use-toast-store/useToastStore.ts'
import { ADDON_GROUP_TYPE } from '@/domains/pjazza/types/consts/AddonGroupType.consts.ts'
import { basketLineKey } from '@/domains/pjazza/utils/basketLineKey.ts'
import { formatMenuItemPrice } from '@/domains/pjazza/utils/formatMenuItemPrice.ts'
import { formatMinorAmount } from '@/domains/pjazza/utils/formatMinorAmount.ts'

/**
 * One dish, its choices, and the way into the cart.
 *
 * A sheet at every width rather than a sheet on mobile and a dialog on
 * desktop: the two needed separate styling and drifted, and the desktop dialog
 * was the only element in the app with a container and a shadow.
 *
 * `existingLine` is the one basket line this dish already resolves to — null
 * when it has none yet, or when it has several and picking one to edit would
 * be a guess (see the count badge's own comment in Menu/Show.vue). Reopening
 * an unambiguous dish edits that line instead of stacking another one.
 */
const props = defineProps<PjItemSheetProps>()

const visible = defineModel<boolean>('visible', { default: false })

const basket = useBasketStore()

const toast = useToastStore()

const selection = useItemSelection(toRef(props, 'item'), toRef(props, 'addonGroups'), toRef(props, 'existingLine'))

const imageFailed = ref(false)

watch(
  () => props.item?.id,
  () => (imageFailed.value = false)
)

const isEditing = computed(() => props.existingLine !== null)

/** False for a dish sold in the venue only (DEC-2026-083): the sheet shows it, and staff take the order. */
const canAdd = computed(() => props.item?.isOrderable ?? false)

/** The choices the sheet offers: none on a dish the diner cannot add. */
const optionGroups = computed(() => (canAdd.value ? props.addonGroups : []))

const priceLabel = computed(() => (props.item ? formatMenuItemPrice(props.item) : ''))

const saveLabel = computed(() => {
  if (!props.item) return isEditing.value ? 'Update' : 'Add'

  const amount = formatMinorAmount(selection.total.value, props.item.price.currency)

  return isEditing.value ? `Update · ${amount}` : `Add · ${amount}`
})

const hintFor = (group: OrderingMenuAddonGroup): string => {
  if ((group.min ?? 0) > 0) return group.type === ADDON_GROUP_TYPE.SINGLE ? 'Pick one' : `Pick at least ${group.min}`

  return group.max ? `Up to ${group.max}` : 'Optional'
}

// A full multi-select greys the rest rather than silently ignoring a tap.
const isDisabled = (group: OrderingMenuAddonGroup, addonId: string): boolean =>
  group.type === ADDON_GROUP_TYPE.MULTIPLE &&
  group.max !== null &&
  !selection.isSelected(addonId) &&
  group.items.filter(addon => selection.isSelected(addon.id)).length >= group.max

const handleSave = (): void => {
  if (!props.item) return

  selection.markAttempted()

  // An invalid selection keeps the sheet open with the offending group
  // flagged, rather than bouncing off a server 422 after a round trip. The
  // inline flag is quiet by design and easy to tap straight past, so a toast
  // says the same thing louder — the one line naming what is still missing.
  if (!selection.isValid.value) {
    const problem = selection.firstError.value

    if (problem) toast.warn(`${problem.group}: ${problem.message}`)

    return
  }

  const notes = selection.notes.value.trim() || null

  // The choices on screen still resolve to the line we opened — a plain
  // quantity change, nothing to re-key. Updating it in place beats a
  // remove-then-add for the same reason setQuantity exists at all.
  if (props.existingLine && basketLineKey(props.item.id, selection.choices.value, notes) === props.existingLine.key) {
    basket.setQuantity(props.existingLine.key, selection.quantity.value)
    visible.value = false

    return
  }

  // Only closes if the line actually landed. A refused add means the diner is
  // signed out and a sign-in is about to open over this — the selection is parked
  // and replayed, so tearing the sheet down first would drop the context. The old
  // line comes out only once the new one is confirmed in, never before.
  const added = basket.add({
    item: props.item,
    addonGroups: props.addonGroups,
    quantity: selection.quantity.value,
    addons: selection.choices.value,
    notes
  })

  if (added) {
    if (props.existingLine) basket.remove(props.existingLine.key)

    visible.value = false
  }
}
</script>
